import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { LockIcon } from "@wato/ui";
import { FlowErrorsNavBadge } from "../flow-errors/FlowErrorsNavBadge";
import type { NavItem } from "./nav-config";

const tabClass = (active: boolean) =>
  active
    ? "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-action-primary px-4 text-sm font-semibold text-on-action-primary"
    : "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm text-fg-muted hover:bg-surface-sunken hover:text-fg";

const menuItemClass =
  "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-surface-sunken";

const GROUP_ORDER = ["ai", "replies", "messages"] as const;
const GROUP_LABEL = {
  ai: "dashboard.nav.sectionGroup.ai",
  replies: "dashboard.nav.sectionGroup.replies",
  messages: "dashboard.nav.sectionGroup.messages",
} as const;

/**
 * FB3-22: the other pages of the active sidebar section, as tabs above the content. Pages that are
 * no longer in the sidebar stay one click away here. FB2-05: a locked page stays a tab, marked
 * with a lock and the reason, and links to its unlock page.
 */
export function SectionTabs({
  workspaceId,
  items,
  rel,
}: {
  workspaceId: string;
  items: readonly NavItem[];
  rel: string;
}) {
  const { t } = useI18n();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const moreRef = useRef<HTMLDetailsElement>(null);
  // HUNT-05: keep the active tab visible inside the scrolling strip.
  // OWN-140: the "More" menu closes on navigation and on a click outside it.
  useEffect(() => {
    if (moreRef.current) moreRef.current.open = false;
  }, [location.key]);
  useEffect(() => {
    const onDown = (e: Event) => {
      const el = moreRef.current;
      if (el?.open && !el.contains(e.target as Node)) el.open = false;
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);
  useEffect(() => {
    const active = navRef.current?.querySelector('[aria-current="page"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ block: "nearest", inline: "center" });
    }
  });
  // HUNT-05: fade the edges only while the strip really overflows (any width, not just mobile).
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);
  if (items.length < 2) return null;
  const hostOf = (i: NavItem): string =>
    i.tabHost && items.some((h) => h.to === i.tabHost) ? i.tabHost : i.to;
  // OWN-178: a page that shares a host tab (sequences in "Auto messages") gets no tab of its own.
  const shown = items.filter((i) => hostOf(i) === i.to);
  const main = shown.filter((i) => !i.advanced);
  const advanced = shown.filter((i) => i.advanced);
  const base = `/w/${workspaceId}`;
  const current = items
    .filter((i) => (i.end ? rel === i.to : rel === i.to || rel.startsWith(`${i.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)
    .map(hostOf)[0];
  // OWN-174: sections whose tabs carry `group` render as a grouped card instead of the flat strip.
  const grouped = main.some((i) => i.group);
  const renderTab = (item: NavItem, inMenu = false) => {
    const slug = item.to.replace(/\W+/g, "-");
    return (
      <li key={item.to}>
        <Link
          to={`${base}/${item.locked ? item.locked.to : item.to}`}
          aria-current={item.to === current ? "page" : undefined}
          data-locked={item.locked ? "true" : undefined}
          aria-labelledby={item.locked ? `tab-lock-label-${slug}` : undefined}
          aria-describedby={item.locked ? `tab-lock-reason-${slug}` : undefined}
          className={inMenu ? menuItemClass : tabClass(item.to === current)}
        >
          {item.locked ? <LockIcon className="size-4 shrink-0" /> : null}
          <span id={item.locked ? `tab-lock-label-${slug}` : undefined}>{t(item.label)}</span>
          {item.badge === "flowErrors" && !item.locked ? (
            <FlowErrorsNavBadge workspaceId={workspaceId} />
          ) : null}
          {item.locked ? (
            <span id={`tab-lock-reason-${slug}`} className="text-xs font-normal text-fg-muted">
              {t(item.locked.reasonKey)}
            </span>
          ) : null}
        </Link>
      </li>
    );
  };
  if (grouped) {
    // OWN-140: one compact underline tab bar. The few `essential` tabs keep a stable order; the
    // active tab is always in the bar (a "more" page is appended last); the rest sit once, grouped,
    // in a single "More" menu.
    const essentials = main.filter((i) => i.essential);
    const barItems = essentials.length > 0 ? essentials : main;
    const activeExtra = [...main, ...advanced].find(
      (i) => i.to === current && !barItems.includes(i),
    );
    const bar = activeExtra ? [...barItems, activeExtra] : barItems;
    const rest = [...main, ...advanced].filter((i) => !bar.includes(i));
    const menuBlocks: { key: string; label?: string; list: NavItem[] }[] = [
      { key: "lead", list: rest.filter((i) => !i.advanced && !i.group) },
      ...GROUP_ORDER.map((g) => ({
        key: g,
        label: t(GROUP_LABEL[g]),
        list: rest.filter((i) => !i.advanced && i.group === g),
      })),
      { key: "advanced", label: t("dashboard.nav.advanced"), list: rest.filter((i) => i.advanced) },
    ].filter((b) => b.list.length > 0);
    return (
      <nav
        ref={navRef}
        aria-label={t("dashboard.nav.sectionTabs")}
        className="mb-4 flex items-center"
        data-testid="section-tabs"
      >
        <ul className="flex min-w-0 flex-1 gap-1 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {bar.map((i) => renderTab(i))}
        </ul>
        {menuBlocks.length > 0 ? (
          <details ref={moreRef} className="relative shrink-0" data-testid="section-tabs-more">
            <summary className="inline-flex h-9 cursor-pointer list-none items-center gap-1 rounded-full px-4 text-sm text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
              {t("dashboard.nav.sectionMore")}
              <span aria-hidden="true">▾</span>
            </summary>
            <div className="absolute end-0 top-full z-20 mt-1 flex max-h-80 min-w-56 flex-col gap-2 overflow-y-auto rounded-2xl border border-line bg-surface p-2 shadow-md">
              {menuBlocks.map((b) => (
                <section key={b.key} aria-label={b.label} className="flex flex-col gap-0.5">
                  {b.label ? (
                    <h3 className="px-2 text-xs font-semibold text-fg-muted">{b.label}</h3>
                  ) : null}
                  <ul className="flex flex-col">{b.list.map((i) => renderTab(i, true))}</ul>
                </section>
              ))}
            </div>
          </details>
        ) : null}
      </nav>
    );
  }
  return (
    <nav
      ref={navRef}
      aria-label={t("dashboard.nav.sectionTabs")}
      className="mb-4 overflow-x-auto overscroll-x-contain [mask-image:linear-gradient(to_right,transparent,#000_1.5rem,#000_calc(100%-1.5rem),transparent)] pb-px data-[overflow=false]:[mask-image:none]"
      data-overflow={overflowing ? "true" : "false"}
      data-testid="section-tabs"
    >
      <ul className="flex min-w-max gap-1">{main.map((i) => renderTab(i))}</ul>
      {advanced.length > 0 ? (
        <details className="pb-2" open={advanced.some((i) => i.to === current)}>
          <summary className="cursor-pointer px-3 py-1 text-sm text-fg-muted">
            {t("dashboard.nav.advanced")}
          </summary>
          <ul className="flex min-w-max gap-1">{advanced.map((i) => renderTab(i))}</ul>
        </details>
      ) : null}
    </nav>
  );
}
